/**
 * Sales & Growth (WALLIYO-0132).
 *
 * Leads are pros who are not on the platform yet — the list that fills the
 * gaps Marketplace Operations surfaces. Campaigns are messages to the pros and
 * customers we do have. Referrals are pros brought by other pros.
 *
 * The campaign form leads with the channel and says what each costs, because
 * in-app is free and every SMS is money, and data in Guinea is paid by the
 * hour. Sending is a separate button from drafting for the same reason.
 */

import { CATEGORIES, type CategoryKey } from "@wallyo/contracts/categories";
import type { Campaign, CampaignChannel, Lead, LeadSource, LeadStatus } from "@wallyo/contracts/console/growth";
import { useState } from "react";

import {
  addLead,
  createCampaign,
  listCampaigns,
  listLeads,
  listReferrals,
  moveLead,
  sendCampaign,
} from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

const tradeName = (key: CategoryKey): string => CATEGORIES[key]?.label ?? key;
const TRADES = Object.keys(CATEGORIES) as CategoryKey[];

const LEAD_TONE: Record<LeadStatus, string> = {
  new: "warn",
  contacted: "info",
  signed_up: "ok",
  lost: "",
};

// ── Leads ────────────────────────────────────────────────────────────────

export function LeadsScreen() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [reads, setReads] = useState(0);
  const [adding, setAdding] = useState(false);
  const load = useLoad(() => listLeads(client), String(reads));
  const leads = load.state.data?.leads ?? [];
  const reload = () => setReads((count) => count + 1);

  const move = (lead: Lead, status: LeadStatus) => {
    void moveLead(client, lead.leadId, { status, version: lead.version, reason: `Moved to ${status}` }).then(
      () => {
        alerts.ok(t("leads.moved", { name: lead.name }));
        reload();
      },
      (error: unknown) => alerts.problem(error),
    );
  };

  return (
    <>
      <PageTop title={t("leads.title")} sub={t("leads.sub")} />
      <div className="hr-search">
        <div className="grow" />
        <button className="btn primary" type="button" onClick={() => setAdding(true)}>
          {t("leads.add")}
        </button>
      </div>

      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("leads.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!load.state.data && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}

      {load.state.data ? (
        <section className="card">
          {leads.length === 0 ? (
            <div className="empty">{t("leads.none")}</div>
          ) : (
            <ul className="hr-plain">
              {leads.map((lead) => (
                <li key={lead.leadId} className="role-holder">
                  <strong>{lead.name}</strong>
                  <span className="mono foot">{lead.phone}</span>
                  <span className="foot">
                    {tradeName(lead.category)}
                    {lead.city ? ` · ${lead.city}` : ""}
                  </span>
                  <span className={`pill ${LEAD_TONE[lead.status]}`}>{t(`leads.status.${lead.status}` as never)}</span>
                  <div className="grow" />
                  {lead.status === "new" ? (
                    <button className="btn quiet small" type="button" onClick={() => move(lead, "contacted")}>
                      {t("leads.markContacted")}
                    </button>
                  ) : null}
                  {lead.status === "contacted" ? (
                    <button className="btn quiet small" type="button" onClick={() => move(lead, "lost")}>
                      {t("leads.markLost")}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {adding ? (
        <AddLeadDialog
          onClose={() => setAdding(false)}
          onDone={(lead) => {
            setAdding(false);
            alerts.ok(t("leads.added", { name: lead.name }));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function AddLeadDialog({ onClose, onDone }: { onClose: () => void; onDone: (lead: Lead) => void }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState<CategoryKey>("plumber");
  const [city, setCity] = useState("");
  const [source, setSource] = useState<LeadSource>("coverage_gap");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("leads.addTitle")}
      body={<p className="meta">{t("leads.addBody")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("leads.addSubmit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          name.trim().length < 2 ? t("leads.nameNeeded") : null,
          (key) =>
            addLead(
              client,
              {
                name: name.trim(),
                phone: phone.trim(),
                category,
                city: city.trim() || null,
                source,
                reason: reason.trim(),
              },
              { idempotencyKey: key },
            ),
          ({ lead }) => onDone(lead),
        )
      }
    >
      <div className="field">
        <label htmlFor="lead-name">{t("leads.field.name")}</label>
        <input
          id="lead-name"
          value={name}
          onChange={(event) => {
            write.edited();
            setName(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="lead-phone">{t("leads.field.phone")}</label>
        <input id="lead-phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="lead-category">{t("leads.field.trade")}</label>
        <select
          id="lead-category"
          value={category}
          onChange={(event) => setCategory(event.target.value as CategoryKey)}
        >
          {TRADES.map((key) => (
            <option key={key} value={key}>
              {tradeName(key)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="lead-city">{t("leads.field.city")}</label>
        <input id="lead-city" value={city} onChange={(event) => setCity(event.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="lead-source">{t("leads.field.source")}</label>
        <select id="lead-source" value={source} onChange={(event) => setSource(event.target.value as LeadSource)}>
          <option value="coverage_gap">{t("leads.source.coverage_gap")}</option>
          <option value="referral">{t("leads.source.referral")}</option>
          <option value="inbound">{t("leads.source.inbound")}</option>
          <option value="other">{t("leads.source.other")}</option>
        </select>
      </div>
    </ReasonDialogView>
  );
}

// ── Campaigns ────────────────────────────────────────────────────────────

export function CampaignsScreen() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [reads, setReads] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const load = useLoad(() => listCampaigns(client), String(reads));
  const campaigns = load.state.data?.campaigns ?? [];
  const reload = () => setReads((count) => count + 1);

  const send = (campaign: Campaign) => {
    void sendCampaign(client, campaign.campaignId, { version: campaign.version, reason: "Sending" }).then(
      ({ campaign: sent }) => {
        alerts.ok(t("campaigns.sent", { count: String(sent.recipients ?? 0) }));
        reload();
      },
      (error: unknown) => alerts.problem(error),
    );
  };

  return (
    <>
      <PageTop title={t("campaigns.title")} sub={t("campaigns.sub")} />
      <div className="hr-search">
        <div className="grow" />
        <button className="btn primary" type="button" onClick={() => setDrafting(true)}>
          {t("campaigns.draft")}
        </button>
      </div>

      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("campaigns.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}

      {load.state.data ? (
        <section className="card">
          {campaigns.length === 0 ? (
            <div className="empty">{t("campaigns.none")}</div>
          ) : (
            <ul className="hr-plain">
              {campaigns.map((campaign) => (
                <li key={campaign.campaignId} className="role-holder">
                  <span className="mono foot">{campaign.reference}</span>
                  <strong>{campaign.name}</strong>
                  <span className={campaign.channel === "sms" ? "pill warn" : "pill"}>
                    {t(`campaigns.channel.${campaign.channel}` as never)}
                  </span>
                  <span className="foot">{t(`campaigns.audience.${campaign.audience}` as never)}</span>
                  <div className="grow" />
                  {campaign.status === "sent" ? (
                    <span className="foot">
                      {t("campaigns.wentTo", { count: String(campaign.recipients ?? 0) })} ·{" "}
                      {formatDateTime(campaign.sentAt!)}
                    </span>
                  ) : (
                    <button className="btn small" type="button" onClick={() => send(campaign)}>
                      {t("campaigns.send")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {drafting ? (
        <DraftCampaignDialog
          onClose={() => setDrafting(false)}
          onDone={(campaign, wouldReach) => {
            setDrafting(false);
            alerts.ok(t("campaigns.drafted", { reference: campaign.reference, count: String(wouldReach) }));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function DraftCampaignDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (campaign: Campaign, wouldReach: number) => void;
}) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [name, setName] = useState("");
  const [channel, setChannel] = useState<CampaignChannel>("in_app");
  const [audience, setAudience] = useState<"pros" | "customers">("pros");
  const [onlyInactive, setOnlyInactive] = useState(true);
  const [bodyFr, setBodyFr] = useState("");
  const [budget, setBudget] = useState("");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("campaigns.draftTitle")}
      body={<p className="meta">{t("campaigns.draftBody")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("campaigns.draftSubmit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          channel === "sms" && Number(budget) <= 0 ? t("campaigns.budgetNeeded") : null,
          (key) =>
            createCampaign(
              client,
              {
                name: name.trim(),
                channel,
                audience,
                onlyInactive,
                bodyFr: bodyFr.trim(),
                smsBudgetGnf: channel === "sms" ? Number(budget) : null,
                reason: reason.trim(),
              },
              { idempotencyKey: key },
            ),
          ({ campaign, wouldReach }) => onDone(campaign, wouldReach),
        )
      }
    >
      <div className="field">
        <label htmlFor="campaign-name">{t("campaigns.field.name")}</label>
        <input
          id="campaign-name"
          value={name}
          onChange={(event) => {
            write.edited();
            setName(event.target.value);
          }}
        />
      </div>
      {/* Channel first, and what each costs said plainly. */}
      <div className="field">
        <label htmlFor="campaign-channel">{t("campaigns.field.channel")}</label>
        <select
          id="campaign-channel"
          value={channel}
          onChange={(event) => setChannel(event.target.value as CampaignChannel)}
        >
          <option value="in_app">{t("campaigns.channel.in_app")}</option>
          <option value="sms">{t("campaigns.channel.sms")}</option>
        </select>
        <span className="meta">{channel === "sms" ? t("campaigns.smsCosts") : t("campaigns.inAppFree")}</span>
      </div>
      {channel === "sms" ? (
        <div className="field">
          <label htmlFor="campaign-budget">{t("campaigns.field.budget")}</label>
          <input
            id="campaign-budget"
            type="number"
            min="1"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
          />
          <span className="meta">{t("campaigns.field.budgetHint")}</span>
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="campaign-audience">{t("campaigns.field.audience")}</label>
        <select
          id="campaign-audience"
          value={audience}
          onChange={(event) => setAudience(event.target.value as "pros" | "customers")}
        >
          <option value="pros">{t("campaigns.audience.pros")}</option>
          <option value="customers">{t("campaigns.audience.customers")}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="campaign-inactive">
          <input
            id="campaign-inactive"
            type="checkbox"
            checked={onlyInactive}
            onChange={(event) => setOnlyInactive(event.target.checked)}
          />{" "}
          {t("campaigns.onlyInactive")}
        </label>
      </div>
      <div className="field">
        <label htmlFor="campaign-body">{t("campaigns.field.body")}</label>
        <textarea id="campaign-body" rows={3} value={bodyFr} onChange={(event) => setBodyFr(event.target.value)} />
        <span className="meta">
          {channel === "sms"
            ? t("campaigns.smsLength", { count: String(bodyFr.length) })
            : t("campaigns.field.bodyHint")}
        </span>
      </div>
    </ReasonDialogView>
  );
}

// ── Referrals ────────────────────────────────────────────────────────────

export function ReferralsScreen() {
  const { client } = useApi();
  const load = useLoad(() => listReferrals(client), "referrals");
  const data = load.state.data;

  return (
    <>
      <PageTop title={t("referrals.title")} sub={t("referrals.sub")} />

      {load.state.status === "failed" && !data ? (
        <LoadFailed
          message={t("referrals.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}

      {data ? (
        <section className="card">
          {data.paidOut.length > 0 ? (
            <p className="foot">
              {data.paidOut
                .map((payout) => t("referrals.paidOut", { amount: String(payout.amount), currency: payout.currency }))
                .join(" · ")}
            </p>
          ) : null}
          {data.referrals.length === 0 ? (
            <div className="empty">{t("referrals.none")}</div>
          ) : (
            <ul className="hr-plain">
              {data.referrals.map((referral) => (
                <li key={referral.referralId} className="role-holder">
                  <span className="mono foot">{referral.code}</span>
                  <strong>{referral.referrerName}</strong>
                  <span className="foot">
                    {referral.referredName ? `→ ${referral.referredName}` : t("referrals.unused")}
                  </span>
                  <span className={referral.status === "rewarded" ? "pill ok" : "pill"}>
                    {t(`referrals.status.${referral.status}` as never)}
                  </span>
                  <div className="grow" />
                  <span className="mono foot">
                    {referral.rewardAmount} {referral.rewardCurrency}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="foot">{t("referrals.paidWhen")}</p>
        </section>
      ) : null}
    </>
  );
}
